import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, Drawer, IconButton, InputAdornment, MenuItem,
  Skeleton, Stack, TextField, Tooltip, Typography, FormControlLabel, Switch,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { failureLabel } from "../shared/failureLabels";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import { useNotifier } from "../../../Core/Notifier";
import { B13, B13Ink, B13TypeScale, tint12 } from "../../../theme/b13Tokens";
import { surfaceSx } from "../../Course/shared/moduleSx";
import {
  HistoryChannel, HistoryRow, HistoryStatus, fetchHistoryItem, listHistory,
} from "../../../Api/communicationHistoryApi";

const filterFieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 40, borderRadius: "10px", bgcolor: "#FFFFFF",
    "& fieldset": { borderColor: B13.line },
    "&:hover fieldset": { borderColor: tint12(B13.primary) },
    "&.Mui-focused fieldset": { borderColor: B13.primary, borderWidth: "1px" },
  },
  "& .MuiOutlinedInput-input": { fontSize: 13.5, color: B13.ink },
} as const;

const STATUS_STYLE: Record<string, { bg: string; fg: string; label?: string }> = {
  queued:     { bg: "#FEF3C7", fg: "#92400E" },
  sent:       { bg: "#DCFCE7", fg: "#15803D" },
  delivered:  { bg: "#DCFCE7", fg: "#15803D" },
  failed:     { bg: "#FEE2E2", fg: "#B91C1C" },
  suppressed: { bg: "#F1F5F9", fg: "#475569", label: "Skipped" },
  cancelled:  { bg: "#F1F5F9", fg: "#475569" },
};

const CommunicationHistoryPage: React.FC = () => {
  const { showErrorMessage } = useNotifier();

  const [rows, setRows]           = useState<HistoryRow[]>([]);
  const [loading, setLoading]     = useState(true);
  const [total, setTotal]         = useState(0);
  const [lastPage, setLastPage]   = useState(1);

  const [search, setSearch]       = useState("");
  const [channel, setChannel]     = useState<HistoryChannel | "">("");
  const [status, setStatus]       = useState<HistoryStatus | "">("");
  const [campaignId, setCampaignId] = useState<string>("");
  const [dateFrom, setDateFrom]   = useState<string>("");
  const [dateTo, setDateTo]       = useState<string>("");
  const [includeTest, setIncludeTest] = useState(false);
  const [debounced, setDebounced] = useState("");
  const [page, setPage]           = useState(1);
  const [perPage, setPerPage]     = useState<number>(25);

  const [detail, setDetail]       = useState<HistoryRow | null>(null);

  // Any filter change resets to page 1.
  useEffect(() => { setPage(1); }, [debounced, channel, status, campaignId, dateFrom, dateTo, includeTest, perPage]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listHistory({
        search: debounced || undefined,
        channel: channel || undefined,
        status:  status  || undefined,
        campaign_id: campaignId ? Number(campaignId) : undefined,
        date_from: dateFrom || undefined,
        date_to:   dateTo   || undefined,
        include_test: includeTest || undefined,
        page,
        per_page: perPage,
      });
      setRows(res.data);
      setTotal(res.total);
      setLastPage(res.last_page || 1);
    } catch (e: any) {
      showErrorMessage(e?.message || "Failed to load communication history.");
    } finally { setLoading(false); }
  }, [debounced, channel, status, campaignId, dateFrom, dateTo, includeTest, page, perPage, showErrorMessage]);

  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => ({
    total: total,
    email: rows.filter((r) => r.channel === "email").length,
    sms:   rows.filter((r) => r.channel === "sms").length,
    failed: rows.filter((r) => r.status === "failed").length,
  }), [rows, total]);

  const openDetail = async (row: HistoryRow) => {
    try {
      const full = await fetchHistoryItem(row.channel, row.id);
      setDetail(full);
    } catch (e: any) {
      showErrorMessage(e?.message || "Failed to load detail.");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#F7F9FC", minHeight: "100%" }}>
      {/* Header */}
      <Box sx={{ ...surfaceSx, p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}
               alignItems={{ xs: "flex-start", md: "center" }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2.5, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            bgcolor: tint12(B13.primary), color: B13.primary,
            "& svg": { fontSize: 24 },
          }}><HistoryOutlinedIcon /></Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ ...B13TypeScale.h2, letterSpacing: "-0.01em" }}>
              Communication History
            </Typography>
            <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2, maxWidth: 720 }}>
              Email and SMS activity across Step Up Academy. Delivery status shown is based on
              provider acknowledgement — see per-row tooltip for open-tracking caveats.
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Summary */}
      <Box sx={{ display: "grid",
                 gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
                 gap: 1.5, mb: 2 }}>
        {[
          { label: "Total shown", value: counts.total,  tone: B13.primary },
          { label: "Email",       value: counts.email,  tone: "#1F6FEB" },
          { label: "SMS",         value: counts.sms,    tone: "#22c55e" },
          { label: "Failed",      value: counts.failed, tone: "#B91C1C" },
        ].map((c) => (
          <Box key={c.label} sx={{ ...surfaceSx, p: 2, borderRadius: 3 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: B13Ink.ink3, textTransform: "uppercase", letterSpacing: 0.6 }}>
              {c.label}
            </Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: c.tone, mt: 0.5, letterSpacing: "-0.02em" }}>
              {c.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{ ...surfaceSx, p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}
               alignItems={{ xs: "stretch", md: "center" }}>
          <TextField size="small"
            placeholder="Search subject, message, recipient…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ ...filterFieldSx, flex: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: B13Ink.ink3 }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField select size="small" label="Channel" value={channel}
            onChange={(e) => setChannel(e.target.value as any)}
            sx={{ ...filterFieldSx, minWidth: 150 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="email">Email</MenuItem>
            <MenuItem value="sms">SMS</MenuItem>
          </TextField>
          <TextField select size="small" label="Status" value={status}
            onChange={(e) => setStatus(e.target.value as any)}
            sx={{ ...filterFieldSx, minWidth: 160 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="queued">Queued</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="suppressed">Suppressed</MenuItem>
          </TextField>
          <TextField size="small" label="Campaign #" value={campaignId}
            onChange={(e) => setCampaignId(e.target.value.replace(/[^0-9]/g, ""))}
            sx={{ ...filterFieldSx, width: 130 }} />
          <TextField size="small" type="date" label="From"
            InputLabelProps={{ shrink: true }}
            value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            sx={{ ...filterFieldSx, width: 150 }} />
          <TextField size="small" type="date" label="To"
            InputLabelProps={{ shrink: true }}
            value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            sx={{ ...filterFieldSx, width: 150 }} />
          <FormControlLabel control={
            <Switch checked={includeTest} onChange={(_, v) => setIncludeTest(v)} />
          } label={<Typography sx={{ fontSize: 12.5 }}>Include test sends</Typography>} />
        </Stack>
      </Box>

      {/* Table */}
      <Box sx={{ ...surfaceSx, borderRadius: 3, overflow: "hidden" }}>
        {loading ? (
          <Stack spacing={1} sx={{ p: 2 }}>
            {[0,1,2,3,4].map((i) =>
              <Skeleton key={i} variant="rounded" height={56} sx={{ borderRadius: "10px" }} />
            )}
          </Stack>
        ) : rows.length === 0 ? (
          <Box sx={{ p: { xs: 4, md: 6 }, textAlign: "center" }}>
            <Typography sx={{ ...B13TypeScale.h3, mb: 0.5 }}>No history yet</Typography>
            <Typography sx={{ ...B13TypeScale.body, color: B13Ink.ink2 }}>
              Send a campaign or a direct message to see communication activity here.
            </Typography>
          </Box>
        ) : (
          <Stack divider={<Box sx={{ borderBottom: `1px solid ${B13.line}` }} />}>
            {rows.map((r) => {
              const Icon = r.channel === "email" ? EmailOutlinedIcon : SmsOutlinedIcon;
              const tone = r.channel === "email" ? "#1F6FEB" : "#22c55e";
              const st = STATUS_STYLE[r.status] ?? STATUS_STYLE.sent;
              return (
                <Box key={`${r.channel}-${r.id}`}
                  onClick={() => openDetail(r)}
                  sx={{
                    px: { xs: 1.5, md: 2 }, py: 1.5, cursor: "pointer",
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "180px 1fr 200px 120px" },
                    gap: { xs: 1, md: 2 }, alignItems: "center",
                    transition: "background-color 120ms ease",
                    "&:hover": { bgcolor: "#F8FAFC" },
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: B13Ink.ink2 }}>
                    {new Date(r.created_at).toLocaleString()}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: 2,
                               bgcolor: `${tone}1A`, color: tone,
                               display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Icon fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: B13.ink }} noWrap>
                        {r.subject || (r.channel === "sms" ? "SMS message" : "(no subject)")}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: B13Ink.ink2 }} noWrap>
                        {r.body_preview || "—"}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography sx={{ fontSize: 12.5, color: B13Ink.ink2 }} noWrap>
                    {r.recipient_address || "—"}
                    {r.is_test && (
                      <Chip size="small" label="Test" sx={{
                        ml: 1, height: 18, fontSize: 10, fontWeight: 700,
                        bgcolor: "#E0E7FF", color: "#3730A3",
                      }} />
                    )}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip size="small" label={(st.label ?? r.status)}
                      sx={{ height: 22, fontSize: 11, fontWeight: 700,
                            bgcolor: st.bg, color: st.fg,
                            textTransform: "capitalize" }} />
                    {r.channel === "email" && r.opened_at && (
                      <Tooltip title="Open tracking is approximate and may be affected by email privacy features.">
                        <Chip size="small"
                          label={`Opened${r.open_count > 1 ? ` · ${r.open_count}×` : ""}`}
                          sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: "#EDE9FE", color: "#5B21B6" }} />
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
        {rows.length > 0 && (
          <Box sx={{
            px: 2, py: 1.5, borderTop: `1px solid ${B13.line}`,
            display: "flex", flexWrap: "wrap", gap: 2,
            alignItems: "center", justifyContent: "space-between",
          }}>
            <Typography sx={{ fontSize: 12.5, color: B13Ink.ink2 }}>
              Showing {(page - 1) * perPage + 1}
              –{Math.min(page * perPage, total)} of {total}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField select size="small" label="Per page" value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
                sx={{ ...filterFieldSx, width: 110 }}>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </TextField>
              <Button size="small" startIcon={<ChevronLeftIcon />}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                sx={{ textTransform: "none" }}>Prev</Button>
              <Typography sx={{ fontSize: 12.5, color: B13.ink, fontWeight: 700 }}>
                {page} / {lastPage}
              </Typography>
              <Button size="small" endIcon={<ChevronRightIcon />}
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                disabled={page >= lastPage}
                sx={{ textTransform: "none" }}>Next</Button>
            </Stack>
          </Box>
        )}
      </Box>

      {/* Detail drawer */}
      <Drawer anchor="right" open={!! detail} onClose={() => setDetail(null)}
        PaperProps={{ sx: { width: { xs: "100%", md: "min(640px, 90vw)" }, bgcolor: "#F7F9FC" } }}
      >
        {detail && (
          <Box>
            <Box sx={{ px: 3, py: 2, bgcolor: "#fff", borderBottom: `1px solid ${B13.line}`,
                       display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ ...B13TypeScale.h3 }}>{detail.subject || "SMS message"}</Typography>
                <Typography sx={{ ...B13TypeScale.caption, color: B13Ink.ink2 }}>
                  {new Date(detail.created_at).toLocaleString()}
                </Typography>
              </Box>
              <IconButton onClick={() => setDetail(null)}><CloseIcon /></IconButton>
            </Box>
            <Box sx={{ p: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {detail.channel === "email"
                  ? "Open tracking is approximate and may be affected by email privacy features."
                  : "SMS delivery status shows provider acknowledgement only — not verified delivery to the handset."}
              </Alert>
              <Box sx={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 1, fontSize: 13 }}>
                <MetaLabel label="Channel"     value={detail.channel.toUpperCase()} />
                <MetaLabel label="Status"      value={detail.status} />
                <MetaLabel label="Recipient"   value={detail.recipient_address ?? "—"} />
                <MetaLabel label="Sender"      value={detail.sender ?? "—"} />
                <MetaLabel label="Campaign"    value={detail.campaign_id ? `#${detail.campaign_id}` : "—"} />
                <MetaLabel label="Provider"    value={detail.provider ?? "—"} />
                <MetaLabel label="Provider id" value={detail.provider_message_id ?? "—"} />
                {detail.opened_at && <MetaLabel label="First opened" value={new Date(detail.opened_at).toLocaleString()} />}
                {detail.last_opened_at && <MetaLabel label="Last opened" value={new Date(detail.last_opened_at).toLocaleString()} />}
                {detail.open_count > 0 && <MetaLabel label="Open count" value={String(detail.open_count)} />}
                {detail.failure_reason && <MetaLabel label="Failure" value={failureLabel(detail.failure_reason) || detail.failure_reason} />}
              </Box>
              <Box sx={{ mt: 3, p: 2, bgcolor: "#fff", borderRadius: 2, border: `1px solid ${B13.line}` }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: B13Ink.ink3, textTransform: "uppercase", letterSpacing: 0.6, mb: 1 }}>
                  Body
                </Typography>
                <Box sx={{ whiteSpace: "pre-wrap", fontSize: 13.5 }}>
                  {detail.body || detail.body_preview || "(no body)"}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

const MetaLabel: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: B13Ink.ink3, textTransform: "uppercase", letterSpacing: 0.6 }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: 13 }}>{value}</Typography>
  </>
);

export default CommunicationHistoryPage;
